import React from 'react';

import {
  Create,
  SimpleForm,
  SelectInput,
  TextInput,
  required,
  NumberInput,
  maxValue,
  minValue,
  RecordContextProvider,
  ArrayField,
  Datagrid,
  TextField,
  useGetOne,
} from 'react-admin';

import {
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  Typography,
  Box,
} from '@mui/material';

import { useFieldArray, useFormState } from 'react-hook-form';

import Label from '../../customFields/Label';
import AddEntities from './components/AddEntities';
import { boldDataGridStyle } from '../../constants/style/datagridStyles';
import ReferenceEntityField from '../../customFields/ReferenceEntityField';
import Pagination from '../layout/Pagination';
import DeleteEntities from './components/DeleteEntities';
import ColoredScoreField from '../../customFields/ColoredScoreField';
import RiskClassField from '../../customFields/RiskClassField';
import EntityDisplayNameField from '../../customFields/EntityDisplayNameField';

const Entities = () => {
  const { append, fields, remove } = useFieldArray({
    name: 'entities',
    keyName: 'key',
    rules: {
      required: 'This is required',
      minLength: 1,
    },
  });

  const { errors } = useFormState();
  const errorMessage = errors.entities?.root?.message as string | undefined;

  return (
    <RecordContextProvider value={{ entities: fields }}>
      <Card variant="outlined">
        <CardHeader title="Entities" action={<AddEntities append={append} />} />
        <Divider />
        <ArrayField source="entities" perPage={10}>
          <Datagrid sx={boldDataGridStyle} bulkActionButtons={false}>
            <TextField source="type" />
            <ReferenceEntityField source="" label="Display name">
              <EntityDisplayNameField />
            </ReferenceEntityField>
            <ReferenceEntityField source="" linkType={false} label="Risk score">
              <ColoredScoreField source="riskScore" />
            </ReferenceEntityField>
            <ReferenceEntityField source="" linkType={false} label="Risk class">
              <RiskClassField source="riskScore" />
            </ReferenceEntityField>
            <DeleteEntities remove={remove} fields={fields} />
          </Datagrid>
          {errorMessage && (
            <Box paddingX={4} paddingBottom={4}>
              <Typography color="error">{errorMessage}</Typography>
            </Box>
          )}
          <Pagination disableEmptyText />
        </ArrayField>
      </Card>
    </RecordContextProvider>
  );
};

const PingCreate = ({
  caseId,
  resource,
  onSuccess,
  onError,
}: {
  caseId?: string;
  resource?: string;
  onSuccess?: () => void;
  onError?: (e: Error) => void;
}) => {
  const { data } = useGetOne('cases', { id: caseId }, { enabled: !!caseId });

  if (caseId && !data) return null;

  return (
    <Create
      resource={resource}
      mutationOptions={{
        onSuccess,
        onError,
      }}
    >
      <Card variant="outlined">
        <CardContent>
          <SimpleForm mode="all" defaultValues={{ caseId, entities: data.entities }} component={Box}>
            <Grid container spacing={4}>
              <Grid size={12}>
                <Entities />
              </Grid>
              <Grid size={12}>
                <Card variant="outlined">
                  <CardHeader title="Details" />
                  <Divider />
                  <CardContent>
                    <Grid container spacing={1}>
                      <Grid size={12}>
                        <Typography variant="label">Description of ping</Typography>
                      </Grid>
                      <Grid size={12}>
                        <TextInput fullWidth multiline minRows={3} source="description" validate={required()} />
                      </Grid>
                      <Grid size={12}>
                        <Grid container spacing={1}>
                          <Grid size={12}>
                            <Typography variant="label">Score of ping</Typography>
                          </Grid>
                          <Grid size={12}>
                            <NumberInput source="score" validate={[required(), maxValue(100), minValue(0)]} />
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid size={12}>
                        <Grid container spacing={1}>
                          <Grid size={12}>
                            <Label
                              variant="label"
                              title="Rule decreasing timeline"
                              info="The risk decreasing timeline is adjusting the risk of the entity based on a algorithm over the inputted time"
                            />
                          </Grid>
                          <Grid
                            size={{
                              sm: 12,
                              md: 3,
                              lg: 2,
                            }}
                          >
                            <NumberInput
                              source="riskTimeline.amount"
                              fullWidth
                              label="Number"
                              validate={[required(), minValue(0)]}
                            />
                          </Grid>
                          <Grid
                            size={{
                              sm: 12,
                              md: 4,
                              lg: 2,
                            }}
                          >
                            <SelectInput
                              source="riskTimeline.period"
                              choices={[{ id: 'months', name: 'months' },
                                { id: 'days', name: 'days' },
                                { id: 'years', name: 'years' }]}
                              fullWidth
                              label="Unit"
                              validate={required()}
                              helperText={false}
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </SimpleForm>
        </CardContent>
      </Card>
    </Create>
  );
};

export default PingCreate;
